import { log } from '@/log'
import { sortByDisplayLevel } from '@/models/api/display-level'
import { RawTag } from '@/models/api/tags'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { ColorFamily, ColorFamilyList } from '@/models/tag/color-family'
import { getEnabledColorFamilyList } from '@/models/tag/color-family/filter-only-enabled'
import {
  translateDirectly, translateFromEngineTagPathname,
} from '@ajax/vue/components/templates/item-list/condition/contactlens/interaction/api/query-translator'
import {
  FulfilledInteraction,
  Interaction, InteractionState, SectionName,
} from '@ajax/vue/components/templates/item-list/condition/contactlens/interaction/interaction'
import {
  ConditionGroup,
  createCondition,
  createConditionGroup,
  emptyConditionGroup,
} from '@ajax/vue/components/templates/item-list/condition/contactlens/model/condition'
import { TagsService } from '@spa/services/api/tags'

export interface DependenciesForInitialize {
  readonly tagsService: TagsService
  readonly behavior: BehaviorConfig
  readonly location: LocationForInitialize
}
type LocationForInitialize = Pick<Location, 'search'>

export async function initializeInteraction(deps: DependenciesForInitialize): Promise<Interaction> {
  const withTagList = initWithTagList(deps.tagsService)

  const states = await Promise.all([
    withTagList({
      sectionName: 'disposalSchedule',
      sectionLabel: '装用期間',
      tag: lensTag('wear_period'),
      displayKey: 'description',
      queryKey: 'period',
    }),
    withTagList({
      sectionName: 'dia',
      sectionLabel: 'サイズ（DIA）',
      tag: lensTag('dia'),
      displayKey: 'description',
      queryKey: 'dia',
    }),
    withTagList({
      sectionName: 'color',
      sectionLabel: 'カラー',
      tag: productTag('color_family'),
      displayKey: 'title',
      queryKey: 'color_family',
      tagDecorator: (tags: RawTag[]): TagForCondition[] => {
        const colorFamilyList = (() => {
          const validColorFamilies = tags.reduce(
            (acc: ColorFamily[], tag) => ColorFamily
              .createFromDto(tag)
              .map(cf => acc.concat(cf)).getOrElseL(() => {
                log.warn('failed to create color family', { tag })

                return acc
              }),
            []
          )

          return ColorFamilyList.valueOf(validColorFamilies)
        })()

        return getEnabledColorFamilyList(colorFamilyList, deps.behavior).map(cf => ({
          id: cf.id,
          name: cf.name,
          title: cf.title,
          pathname: cf.pathname,
          description: cf.description,
          display_level: cf.displayLevel,
        }))
      },
    }),
    initWithStatic({ sectionName: 'prescriptionOrNot', queryKey: 'power' }, queryKey => createConditionGroup(
      '度あり・なし',
      [
        createCondition('non_lens_power', '度なし', lensTag('non_lens_power')),
        createCondition('lens_power', '度あり', lensTag('lens_power')),
      ],
      translateFromEngineTagPathname(queryKey)
    )),
    withTagList({
      sectionName: 'bc',
      sectionLabel: 'ベースカーブ（BC）',
      tag: lensTag('bc'),
      displayKey: 'description',
      queryKey: 'bc',
    }),
    withTagList({
      sectionName: 'colorDia',
      sectionLabel: '着色直径',
      tag: lensTag('color_dia'),
      displayKey: 'description',
      queryKey: 'color_dia',
    }),
    initWithStatic({ sectionName: 'withBorderOrNot', queryKey: 'border' }, queryKey => createConditionGroup(
      'フチあり・なし',
      [
        createCondition('non_edge', 'フチなし', lensTag('non_edge')),
        createCondition('edge', 'フチあり', lensTag('edge')),
      ],
      translateFromEngineTagPathname(queryKey)
    )),
    // tslint:disable-next-line:ter-arrow-parens
    initWithStatic({ sectionName: 'liquidRetainedRatio', queryKey: 'liquid_rate' }, queryKey => createConditionGroup(
      '含水率',
      [
        createCondition('low-liquid', '低含水', '0~50'),
        createCondition('high-liquid', '高含水', '50~100'),
      ],
      translateDirectly(queryKey)
    )),
    initWithStatic({ sectionName: 'option', queryKey: 'option' }, queryKey => createConditionGroup(
      'オプション',
      [
        createCondition('moist', 'モイスト', lensTag('moist')),
        createCondition('uvcut', 'UVカット', lensTag('uvcut')),
      ],
      translateFromEngineTagPathname(queryKey)
    )),
  ])

  return new FulfilledInteraction(
    states.map(inheritStateFromQuery(deps.location)).reduce(
      (acc, s) => ({
        ...acc,
        [s.sectionName]: s.conditionGroup,
      }),
      {} as InteractionState
    )
  )
}

type TagForCondition = Pick<RawTag, 'id' | 'name' | 'title' | 'pathname' | 'description' | 'display_level'>
interface InitWithTagListParam {
  /** セクションの識別名 */
  readonly sectionName: SectionName
  /** セクションの表記名 */
  readonly sectionLabel: string
  /** 取得するタグのパス名 */
  readonly tag: string
  /** 選択項目表示に参照するタグのキー */
  readonly displayKey: keyof TagForCondition
  /** 絞り込みのクエリで使うキー */
  readonly queryKey: QueryKey

  readonly tagDecorator?: (tags: RawTag[]) => TagForCondition[]
}

type QueryKey =
  | 'period'
  | 'dia'
  | 'color_family'
  | 'power'
  | 'bc'
  | 'color_dia'
  | 'border'
  | 'liquid_rate'
  | 'option'

interface InitializedGroup {
  readonly sectionName: SectionName
  readonly queryKey: QueryKey
  readonly conditionGroup: ConditionGroup
}
const initWithTagList = (tagsService: TagsService) =>
  async (param: InitWithTagListParam): Promise<InitializedGroup> => {
    const tagDecoratorFn = param.tagDecorator || ((tags: RawTag[]) => tags)

    const conditionGroup = (await tagsService.list({ pathname: param.tag })).fold(
      (error: Error) => {
        log.error('failed initialize condition group', {
          sectionLabel: param.sectionLabel,
          tag: param.tag,
          error,
        })

        return emptyConditionGroup
      },
      (tags: RawTag[]) => createConditionGroup(
        param.sectionLabel,
        sortByDisplayLevel.asc.sort(tagDecoratorFn(tags))
          .map(t => createCondition(t.id, `${t[param.displayKey]}`, t.pathname)),
        translateFromEngineTagPathname(param.queryKey)
      )
    )

    return {
      sectionName: param.sectionName,
      queryKey: param.queryKey,
      conditionGroup,
    }
  }
function initWithStatic(
  param: { sectionName: SectionName, queryKey: QueryKey },
  f: (q: string) => ConditionGroup
): Promise<InitializedGroup> {
  return Promise.resolve({
    ...param,
    conditionGroup: f(param.queryKey),
  })
}

const productTag = (path: string) => `mycolor.product.${path}`
const lensTag = (path: string) => productTag(`contactlens.${path}`)

/**
 * クエリで指定されている絞り込み条件を初期状態として反映する
 */
function inheritStateFromQuery(location: LocationForInitialize) {
  const query = new URLSearchParams(location.search)

  return (state: InitializedGroup): InitializedGroup => {
    const queryValue = query.get(state.queryKey)
    if (!queryValue) return state

    return state.conditionGroup.findConditionAsQuery(state.queryKey, queryValue).fold(
      state,
      target => ({
        ...state,
        conditionGroup: state.conditionGroup.change({ target, applied: true }),
      })
    )
  }
}
